import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { inviteSecondParent } from '../api/second-parents.js';
import { ApiError } from '../api/client.js';
import { BusyLabel } from '../components/busy-label.js';

/**
 * Invite another parent (canvas row D, "Parent · invite another parent",
 * approved 17 October): first name, last name, email - the parent part of the
 * coach's "Add a new athlete" - and Send invitation. Back to Account, which
 * shows them as Invited.
 *
 * The first parent's alone; the API refuses anybody else (403).
 */

const FIELD_MESSAGES: Record<string, string> = {
  required: 'This is needed.',
  too_long: 'That is too long.',
  invalid_email: 'That is not an email address.',
};

const REFUSALS: Record<string, string> = {
  email_has_account: 'That email already has an account.',
  email_taken: 'Another family was invited at that email.',
};

export function InviteParentRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: string; code: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setFieldError(null);
    setFormError(null);
    try {
      await inviteSecondParent(getToken, { firstName, lastName, email });
      navigate('/account');
    } catch (cause) {
      if (cause instanceof ApiError && cause.field) {
        setFieldError({ field: cause.field, code: cause.code });
      } else if (cause instanceof ApiError && REFUSALS[cause.code]) {
        setFieldError({ field: 'email', code: cause.code });
      } else if (cause instanceof ApiError && cause.code === 'has_other_parent') {
        // Another tab got there first: Account shows who.
        navigate('/account');
      } else {
        setFormError('That did not work. Try again.');
      }
      setSaving(false);
    }
  }

  const errorFor = (field: string) =>
    fieldError?.field === field
      ? (FIELD_MESSAGES[fieldError.code] ?? REFUSALS[fieldError.code] ?? 'That is not right.')
      : null;

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/account" aria-label="Back to your account">
          ‹
        </Link>
        <h2>Invite another adult</h2>
      </div>

      <form onSubmit={submit} noValidate>
        <div className="panel">
          <Field id="first-name" label="First name" value={firstName} onChange={setFirstName} error={errorFor('first_name')} />
          <Field id="last-name" label="Last name" value={lastName} onChange={setLastName} error={errorFor('last_name')} />
          <Field id="email" label="Email" type="email" value={email} onChange={setEmail} error={errorFor('email')} />
        </div>
        {formError ? <p className="state-note">{formError}</p> : null}
        <button className="primary" type="submit" disabled={saving} aria-busy={saving}>
          <BusyLabel busy={saving} working="Sending…">
            Send invitation
          </BusyLabel>
        </button>
      </form>
    </>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  error: string | null;
  type?: string;
}) {
  return (
    <label className="field" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete="off"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-error" id={`${id}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
